import { Link } from 'react-router-dom';
import { ConfigurationsList } from './pages/ConfigurationsList.jsx';
import { ConfigurationProvider } from './contexts/ConfigurationContext.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { CompanyProvider } from './contexts/CompanyContext';
import { LoginPage } from './pages/LoginPage.jsx';

// src/App.js
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import 'reactflow/dist/style.css';
import { Settings, LogOut, Database, MessageSquare, HardDriveDownload, BookOpen, FileText, Users, Building2, Activity, Unplug, CreditCard, BarChart3, Building, Target, FileSpreadsheet } from 'lucide-react';
import DataExplorerPage from './pages/DataExplorerPage';
import DataSyncPage from './pages/DataSyncPage';
import SkillsPage from './pages/SkillsPage';
import FinancialSummaryPage from './pages/FinancialSummaryPage';
import DataViewsPage from './pages/DataViewsPage';
import ReportsPage from './pages/ReportsPage';
import KpiPage from './pages/KpiPage';
import ModelBuilderPage from './pages/ModelBuilderPage';
import CompanyTopBar from './components/shared/CompanyTopBar';

// Admin pages
import CustomerJourney from './pages/admin/CustomerJourney';
import Organizations from './pages/admin/Organizations';
import OrgDetail from './pages/admin/OrgDetail';
import AutoRefreshMonitor from './pages/admin/AutoRefreshMonitor';
import DisconnectedCompanies from './pages/admin/DisconnectedCompanies';
import Subscriptions from './pages/admin/Subscriptions';
import Usage from './pages/admin/Usage';
import CompanyProfiles from './pages/admin/CompanyProfiles';


function Sidebar() {
  const { logout, isAuthRequired } = useAuth();

  return (
    <div className="w-64 bg-gray-800 h-screen p-4 flex flex-col overflow-y-auto sticky top-0">
      <div className="text-white text-xl font-bold mb-6">Workflow Engine</div>

      {/* Main navigation items */}
      <nav className="flex-grow space-y-2">
<Link to="/data-sync" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <HardDriveDownload className="h-4 w-4 mr-2" />
          Sync
        </Link>
        <Link to="/data-explorer" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <MessageSquare className="h-4 w-4 mr-2" />
          Data Explorer
        </Link>
        <Link to="/data-views" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Database className="h-4 w-4 mr-2" />
          Data Views
        </Link>
        <Link to="/kpis" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Target className="h-4 w-4 mr-2" />
          KPIs
        </Link>
        <Link to="/reports" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <BarChart3 className="h-4 w-4 mr-2" />
          Reports
        </Link>
        <Link to="/skills" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <BookOpen className="h-4 w-4 mr-2" />
          Skills
        </Link>
        <Link to="/financial-summary" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <FileText className="h-4 w-4 mr-2" />
          Financial Summary
        </Link>
        <Link to="/model-builder" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Model Builder
        </Link>
      </nav>

      {/* Admin section */}
      <div className="pt-4 border-t border-gray-700 space-y-2 mb-4">
        <div className="text-gray-400 text-xs uppercase tracking-wider px-3 py-1">Admin</div>
        <Link to="/admin/customer-journey" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Users className="h-4 w-4 mr-2" />
          Customer Journey
        </Link>
        <Link to="/admin/organizations" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Building2 className="h-4 w-4 mr-2" />
          Organizations
        </Link>
        <Link to="/admin/auto-refresh" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Activity className="h-4 w-4 mr-2" />
          Auto-Refresh
        </Link>
        <Link to="/admin/disconnected" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Unplug className="h-4 w-4 mr-2" />
          Disconnected
        </Link>
        <Link to="/admin/subscriptions" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <CreditCard className="h-4 w-4 mr-2" />
          Subscriptions
        </Link>
        <Link to="/admin/usage" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <BarChart3 className="h-4 w-4 mr-2" />
          Usage
        </Link>
        <Link to="/admin/company-profiles" className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center">
          <Building className="h-4 w-4 mr-2" />
          Company Profiles
        </Link>
      </div>

      {/* Settings section at bottom */}
      <div className="pt-4 border-t border-gray-700 space-y-2">
        <Link
          to="/configurations"
          className="block text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center"
        >
          <Settings className="h-4 w-4 mr-2" />
          Configurations
        </Link>
        {isAuthRequired() && (
          <button
            onClick={logout}
            className="w-full text-left text-gray-300 hover:bg-gray-700 px-3 py-2 rounded flex items-center"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </button>
        )}
      </div>
    </div>
  );
}

function AgentLayout({ children }) {
  return (
    <>
      <CompanyTopBar />
      <div className="flex-1 overflow-hidden">
        {children}
      </div>
    </>
  );
}

function ProtectedApp() {
  const { isAuthenticated, isLoading, isAuthRequired } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (isAuthRequired() && !isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <CompanyProvider>
      <BrowserRouter>
        <div className="flex">
          <Sidebar />
          <div className="flex-1 flex flex-col h-screen overflow-hidden bg-gray-900">
            <Routes>
              {/* Agent pages with company top bar */}
              <Route path="/data-explorer" element={<AgentLayout><DataExplorerPage /></AgentLayout>} />
              <Route path="/data-views" element={<AgentLayout><DataViewsPage /></AgentLayout>} />
              <Route path="/kpis" element={<AgentLayout><KpiPage /></AgentLayout>} />
              <Route path="/reports" element={<AgentLayout><ReportsPage /></AgentLayout>} />
              <Route path="/financial-summary" element={<AgentLayout><FinancialSummaryPage /></AgentLayout>} />
              <Route path="/model-builder" element={<AgentLayout><ModelBuilderPage /></AgentLayout>} />

              {/* Other pages without company top bar */}
              <Route path="/" element={<Navigate to="/admin/customer-journey" replace />} />
              <Route path="/configurations" element={<ConfigurationsList />} />
              <Route path="/data-sync" element={<DataSyncPage />} />
              <Route path="/entity-sync" element={<Navigate to="/data-sync" replace />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/admin/customer-journey" element={<CustomerJourney />} />
              <Route path="/admin/organizations" element={<Organizations />} />
              <Route path="/admin/organizations/:orgId" element={<OrgDetail />} />
              <Route path="/admin/auto-refresh" element={<AutoRefreshMonitor />} />
              <Route path="/admin/disconnected" element={<DisconnectedCompanies />} />
              <Route path="/admin/subscriptions" element={<Subscriptions />} />
              <Route path="/admin/usage" element={<Usage />} />
              <Route path="/admin/company-profiles" element={<CompanyProfiles />} />
            </Routes>
          </div>
        </div>
      </BrowserRouter>
    </CompanyProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ConfigurationProvider>
        <ProtectedApp />
      </ConfigurationProvider>
    </AuthProvider>
  );
}

export default App;
